'use client';

// Root error boundary (replaces Next's default "Application error" blank page).
// Catches any client-side exception during hydration/rendering so the user
// always gets a recoverable, branded screen instead of a dead white page.
import { useEffect } from 'react';

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  // Root error boundary: read the persisted user language from state.language
  // (a naive includes() on the store string would also match user.language).
  let language: 'zh-Hant' | 'en' = 'en';
  try {
    const obj = JSON.parse(typeof window !== 'undefined' ? localStorage.getItem('AiLumix-store') || '{}' : '{}');
    language = obj?.state?.language !== 'en' ? 'zh-Hant' : 'en';
  } catch {
    language = 'en';
  }

  useEffect(() => {
    // Keep the error observable in the console for debugging.
    console.error('AiLumix global error boundary:', error);
    // Report the full stack to the API so a crash can be root-caused without
    // the user having to open DevTools. Best-effort; never block recovery.
    try {
      fetch('/api/v1/debug/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: window.location.href,
          message: String(error?.message ?? error ?? ''),
          stack: String(error?.stack ?? ''),
          digest: error?.digest ?? '',
          ua: navigator.userAgent,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignore */
    }
  }, [error]);

  const isZh = language !== 'en';
  const copy = isZh
    ? {
        title: '页面暂时出现异常',
        desc: '可能是网络波动或临时状态引起，重新加载即可恢复。',
        reload: '重新加载',
        back: '返回首页',
      }
    : {
        title: 'Something went wrong',
        desc: 'A temporary error occurred. Reloading usually fixes it.',
        reload: 'Reload',
        back: 'Back to Home',
      };

  return (
    <html lang={isZh ? 'zh-Hant' : 'en'}>
      <body style={{ margin: 0, background: '#0B0E14', color: '#F3F5F9', fontFamily: '-apple-system, "Segoe UI", "PingFang TC", "Microsoft JhengHei", sans-serif' }}>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>{copy.title}</h1>
            <p style={{ fontSize: 14, color: '#A0ABC0', margin: '0 0 24px', lineHeight: 1.6 }}>{copy.desc}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={reset}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: '#6B8AFF', color: '#fff', fontSize: 14, fontWeight: 600,
                }}
              >
                {copy.reload}
              </button>
              <button
                type="button"
                onClick={() => { window.location.href = '/markets'; }}
                style={{
                  padding: '10px 20px', borderRadius: 8, cursor: 'pointer',
                  background: 'transparent', color: '#F3F5F9', fontSize: 14, fontWeight: 500,
                  border: '1px solid #293244',
                }}
              >
                {copy.back}
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
